import React, { useState } from 'react';
import { ProgramConfig, ACDRecord } from '../../../lib/programConfig';
import { Plus, Trash2, Edit2, ShieldCheck, DollarSign, Check, X, Building2 } from 'lucide-react';

interface ProgramAcdEditorProps {
  config: ProgramConfig;
  sponsorsList: any[];
  invoiceTemplatesList?: any[];
  onChange: (updated: ProgramConfig) => void;
}

export default function ProgramAcdEditor({
  config,
  sponsorsList,
  invoiceTemplatesList = [],
  onChange,
}: ProgramAcdEditorProps) {
  const acdRecords = config.acdRecords || [];
  const variables = config.pricingVariables || [];

  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedVarId, setSelectedVarId] = useState<string>('');
  const [selectedSponsorId, setSelectedSponsorId] = useState<string>('');
  const [formActualCost, setFormActualCost] = useState<number | ''>('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [formCode, setFormCode] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const resetForm = () => {
    setEditingId(null);
    setSelectedVarId('');
    setSelectedSponsorId('');
    setFormActualCost('');
    setSelectedTemplateId('');
    setFormCode('');
    setErrorMsg(null);
  };

  const startEdit = (record: ACDRecord) => {
    setEditingId(record.id);
    setSelectedVarId(record.pricingVariableId);
    setSelectedSponsorId(record.sponsorId);
    setFormActualCost(record.actualCost);
    setSelectedTemplateId(record.invoiceTemplateId || '');
    setFormCode(record.acdCode || '');
    setErrorMsg(null);
  };

  const handleSave = () => {
    setErrorMsg(null);
    if (!selectedVarId) {
      setErrorMsg('Please select a Pricing Variable.');
      return;
    }
    if (!selectedSponsorId) {
      setErrorMsg('Please select a Supplier/Sponsor.');
      return;
    }
    if (formActualCost === '' || isNaN(Number(formActualCost))) {
      setErrorMsg('Valid Supplier Cost is required.');
      return;
    }

    // Uniqueness check: Program + Pricing Variable + Sponsor must uniquely identify an ACD relationship
    const duplicate = acdRecords.find(
      (r) =>
        r.pricingVariableId === selectedVarId &&
        r.sponsorId === selectedSponsorId &&
        r.id !== editingId
    );
    if (duplicate) {
      setErrorMsg('An Actual Cost Determination (ACD) relationship already exists for this Pricing Variable and Sponsor combination.');
      return;
    }

    const matchedSponsor = sponsorsList.find((s) => (s.sponsorId || s.id) === selectedSponsorId);
    const sponsorName = matchedSponsor?.name || matchedSponsor?.companyName || matchedSponsor?.displayName || 'Sponsor';

    const now = new Date().toISOString();

    if (editingId) {
      const updated = acdRecords.map((r) => {
        if (r.id === editingId) {
          return {
            ...r,
            programId: config.id,
            pricingVariableId: selectedVarId,
            sponsorId: selectedSponsorId,
            sponsorName,
            actualCost: Number(formActualCost),
            invoiceTemplateId: selectedTemplateId || undefined,
            acdCode: formCode || r.acdCode,
            updatedAt: now,
          };
        }
        return r;
      });
      onChange({ ...config, acdRecords: updated });
    } else {
      const newRecord: ACDRecord = {
        id: `acd_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
        programId: config.id,
        pricingVariableId: selectedVarId,
        sponsorId: selectedSponsorId,
        sponsorName,
        actualCost: Number(formActualCost),
        invoiceTemplateId: selectedTemplateId || undefined,
        acdCode: formCode, // If blank, backend will generate ACD code
        active: true,
        createdAt: now,
        updatedAt: now,
      };
      onChange({ ...config, acdRecords: [...acdRecords, newRecord] });
    }

    resetForm();
  };

  const handleDelete = (id: string) => {
    const updated = acdRecords.filter((r) => r.id !== id);
    onChange({ ...config, acdRecords: updated });
    if (editingId === id) resetForm();
  };

  const toggleActive = (record: ACDRecord) => {
    const updated = acdRecords.map((r) =>
      r.id === record.id ? { ...r, active: !r.active, updatedAt: new Date().toISOString() } : r
    );
    onChange({ ...config, acdRecords: updated });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
          <Building2 className="w-4 h-4 text-brand" /> Actual Cost Determination (ACD) & Price Configuration
        </h3>
        <p className="text-xs text-slate-500 mt-1">
          Link this program and its pricing variables to suppliers/sponsors with partner-specific supplier cost pricing.
          Applicant selling prices remain separate from internal sponsor supplier costs.
        </p>
      </div>

      {errorMsg && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 font-medium">
          {errorMsg}
        </div>
      )}

      {/* Form Box */}
      <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-700">
            {editingId ? 'Edit ACD Record' : 'Add New Actual Cost Determination (ACD)'}
          </span>
          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1"
            >
              <X className="w-3.5 h-3.5" /> Cancel Edit
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">
              Pricing Variable <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedVarId}
              onChange={(e) => setSelectedVarId(e.target.value)}
              className="w-full px-3 py-1.5 text-xs bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-primary"
            >
              <option value="">-- Select Pricing Variable --</option>
              {variables.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name} ({v.code || 'No code'})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">
              Supplier / Sponsor <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedSponsorId}
              onChange={(e) => setSelectedSponsorId(e.target.value)}
              className="w-full px-3 py-1.5 text-xs bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-primary"
            >
              <option value="">-- Select Sponsor --</option>
              {sponsorsList.map((s) => {
                const sId = s.sponsorId || s.id;
                const sName = s.name || s.companyName || s.displayName || sId;
                return (
                  <option key={sId} value={sId}>
                    {sName} {s.code ? `(${s.code})` : ''}
                  </option>
                );
              })}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">
              Supplier Cost ($) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              placeholder="e.g. 750.00"
              value={formActualCost}
              onChange={(e) => setFormActualCost(e.target.value === '' ? '' : Number(e.target.value))}
              className="w-full px-3 py-1.5 text-xs bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-primary"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">
              Optional Invoice Template Link
            </label>
            <select
              value={selectedTemplateId}
              onChange={(e) => setSelectedTemplateId(e.target.value)}
              className="w-full px-3 py-1.5 text-xs bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-primary"
            >
              <option value="">-- None (Default) --</option>
              {invoiceTemplatesList.map((t) => {
                const typesDisplay = Array.isArray(t.types)
                  ? t.types.join('/')
                  : (t.type || 'invoice');
                const codePrefix = t.code ? `[${t.code}] ` : '';
                return (
                  <option key={t.id} value={t.id}>
                    {codePrefix}{t.name} ({typesDisplay})
                  </option>
                );
              })}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">
              ACD Code (Auto generated if blank)
            </label>
            <input
              type="text"
              placeholder="Auto e.g. ACD-1001"
              value={formCode}
              onChange={(e) => setFormCode(e.target.value)}
              className="w-full px-3 py-1.5 text-xs font-mono bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-primary"
            />
          </div>

          <div className="flex items-end">
            <button
              type="button"
              onClick={handleSave}
              className="w-full py-1.5 px-3 bg-brand-primary hover:bg-brand-primary-dark text-white rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors"
            >
              {editingId ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
              {editingId ? 'Update ACD Entry' : 'Add ACD Entry'}
            </button>
          </div>
        </div>
      </div>

      {/* List Table */}
      <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
        <table className="w-full text-left text-xs">
          <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-semibold uppercase tracking-wider">
            <tr>
              <th className="py-2.5 px-3">ACD Code</th>
              <th className="py-2.5 px-3">Pricing Variable</th>
              <th className="py-2.5 px-3">Sponsor / Partner</th>
              <th className="py-2.5 px-3 text-right">Supplier Cost</th>
              <th className="py-2.5 px-3 text-center">Status</th>
              <th className="py-2.5 px-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {acdRecords.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-6 text-center text-slate-500 italic">
                  No Actual Cost Determination records created yet. Add one above.
                </td>
              </tr>
            ) : (
              acdRecords.map((r) => {
                const matchedVar = variables.find((v) => v.id === r.pricingVariableId);
                const matchedSponsor = sponsorsList.find((s) => (s.sponsorId || s.id) === r.sponsorId);
                const sponsorLabel = r.sponsorName || matchedSponsor?.name || matchedSponsor?.companyName || r.sponsorId;

                return (
                  <tr key={r.id} className="hover:bg-slate-50/70 transition-colors">
                    <td className="py-2.5 px-3 font-mono font-medium text-slate-700">
                      <span className="px-1.5 py-0.5 bg-slate-100 rounded text-slate-800 font-semibold">
                        {r.acdCode || 'Pending...'}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 font-semibold text-slate-900">
                      {matchedVar ? matchedVar.name : r.pricingVariableId}
                    </td>
                    <td className="py-2.5 px-3 font-medium text-slate-800 flex items-center gap-1.5">
                      <ShieldCheck className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                      <span>{sponsorLabel}</span>
                    </td>
                    <td className="py-2.5 px-3 text-right font-bold text-slate-900">
                      ${r.actualCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      <button
                        type="button"
                        onClick={() => toggleActive(r)}
                        className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                          r.active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-500'
                        }`}
                      >
                        {r.active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="py-2.5 px-3 text-right space-x-2">
                      <button
                        type="button"
                        onClick={() => startEdit(r)}
                        className="p-1 text-slate-600 hover:text-indigo-600 rounded transition-colors"
                        title="Edit ACD Entry"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(r.id)}
                        className="p-1 text-slate-400 hover:text-red-600 rounded transition-colors"
                        title="Delete ACD Entry"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
